import { FC } from "react";
import Draggable from "react-draggable";
import { Trans } from "react-i18next";
import { CloseIcon, NewChatIcon, SparklesIcon } from "assets/svg";

import { AiAssistantChatHistory, AiAssistantJSONChatMessageType } from "types/aiAssistant";
import { AiChat } from "components/AiAssistantComponents/AiChat";
import { AiChatInput } from "components/AiAssistantComponents/AiChatInput";
import { IAiAssistantSuggestedActionConfig } from "components/AiAssistantComponents/types";

import { Text } from "uikit";

import {
  Container,
  Content,
  Footer,
  GripHorizontalIconStyled,
  Header,
  HeaderLeft,
  HeaderRight,
  IconContainer,
  StyledTooltip,
} from "./styles";

interface IProps {
  data: AiAssistantChatHistory;
  isLoading: boolean;
  onSendMessage: (message: string) => Promise<string | undefined>;
  onSuggestedActionClick: (action: IAiAssistantSuggestedActionConfig) => void;
  onClose: () => void;
  onNewChat: () => void;
  onSubmitButtonClick: (actionType: AiAssistantJSONChatMessageType) => void;
  onCancelButtonClick: () => void;
}

const defaultCoordinates = {
  x: -60,
  y: 100,
};

export const ModalContent: FC<IProps> = ({
  data,
  isLoading,
  onSendMessage,
  onClose,
  onSuggestedActionClick,
  onNewChat,
  onSubmitButtonClick,
  onCancelButtonClick,
}) => {
  const translationPrefix = `ai_assistant_pages.shared.modal`;

  const onStop = () => {
    document.body.style.userSelect = "auto";
  };

  const onStart = () => {
    document.body.style.userSelect = "none";
  };

  return (
    <Draggable
      handle="#handle"
      bounds={"body"}
      onStop={onStop}
      onStart={onStart}
      defaultPosition={defaultCoordinates}
    >
      <Container>
        <Header>
          <HeaderLeft>
            <IconContainer id="handle" data-testid="ai-assistant-modal-grip-horizontal-icon">
              <GripHorizontalIconStyled />
            </IconContainer>

            <SparklesIcon />
            <Text $fs="t16" $fw="semiBold">
              <Trans i18nKey={`${translationPrefix}.title`} />
            </Text>
          </HeaderLeft>

          <HeaderRight>
            <StyledTooltip
              id="new-chat-tooltip"
              tooltipContent={<Trans i18nKey={`${translationPrefix}.new_chat_tooltip`} />}
            >
              <IconContainer onClick={onNewChat} data-testid="ai-assistant-modal-new-chat-icon">
                <NewChatIcon />
              </IconContainer>
            </StyledTooltip>

            <IconContainer onClick={onClose} data-testid="ai-assistant-modal-close-icon">
              <CloseIcon />
            </IconContainer>
          </HeaderRight>
        </Header>

        <Content>
          <AiChat
            data={data}
            isLoading={isLoading}
            onSuggestedActionClick={onSuggestedActionClick}
            onSubmitButtonClick={onSubmitButtonClick}
            onCancelButtonClick={onCancelButtonClick}
          />
        </Content>

        <Footer>
          <AiChatInput onSend={onSendMessage} isLoading={isLoading} />
        </Footer>
      </Container>
    </Draggable>
  );
};
